import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { InventoryService } from 'src/app/business-service/inventory/inventory.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ModalsService } from 'src/app/modules/shared/services/modals/modals.service';
import { ComponentReloadService } from 'src/app/modules/shared/services/reload/component-reload.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
})
export class TermsAndConditionsComponent  implements OnInit {

  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();

  @Input() generatingVoucher :boolean = false;
  @Input() storedFormData: any;
  @Input() editInventory: boolean = false;
  @Input() editQuote: boolean = false;

  @Input() hideFooter: boolean = false;

  @Output() makeFormDirty: EventEmitter<boolean> = new EventEmitter<boolean>();

  itemsForm!: FormGroup;

  constructor(
    private _lovDataService: LovDataService,
    private _itineraryService: ItineraryService,
    private _inventoryService: InventoryService,
    private _responseNotificationService: ResponseNotificationService,
    private _globalService: GlobalService,
    private _reloadService: ComponentReloadService,
    private _modalPopupService: ModalsService,
    private _fb: FormBuilder
  ) { }

  ngOnInit() {
    this.itemsForm = this._fb.group({
      termsAndConditions: [[]],
      note: ['']
    });
    this.itemsForm.valueChanges.subscribe(() => {
      this.makeFormDirty.emit(true);
    })
    if(this.storedFormData){
      this.itemsForm.patchValue(this.storedFormData);
      this.makeFormDirty.emit(false);
    }else{
      this._lovDataService.getLovItineraryData('TermsAndConditions').subscribe(data => {
        // Adding default item prop to LOV data
        data.map((item: { addedByUser: boolean; })=>{
          item.addedByUser = false;
        });
        this.itemsForm.patchValue({
          termsAndConditions: data,
        });
      });
    }
  }

  async onSubmit(): Promise<boolean> {
    try {
      const formData = await this.serializeFormData(this.itemsForm.value);
  
      if (this.leadId) {
        // Lead-specific Terms and Conditions handling
        await this.handleLeadTermsAndConditions(formData);
      } else {
        // Inventory-specific Terms and Conditions handling
        await this.handleInventoryTermsAndConditions(formData);
      }
  
      // Reset form dirty flag
      this.resetFormState();
      return true;
    } catch (error) {
      console.error("Error during form submission:", error);
      // this._responseNotificationService.showErrorMessage("Failed to submit Terms and Conditions. Please try again.");
      return false;
    }
  }
  
  private async handleLeadTermsAndConditions(formData: any): Promise<void> {
    if (this.editQuote) {
      formData.dataSaved = true;
      this.storeFormData.emit(formData); // Emit data to parent component
    } else {
      const data = await this._itineraryService.addTermsAndConditions(formData).toPromise();
      this._responseNotificationService.showMessage("Terms and Conditions Added!");
      data.dataSaved = true;
      this.storeFormData.emit(data); // Emit response data to parent component
    }
  }
  
  private async handleInventoryTermsAndConditions(formData: any): Promise<void> {
    if (this.editInventory) {
      formData.dataSaved = true;
      this.storeFormData.emit(formData); // Emit data to parent component
    } else {
      const data = await this._inventoryService.addInventoryTermsAndConditions(formData).toPromise();
      this._responseNotificationService.showMessage("Terms and Conditions Added!");
      data.dataSaved = true;
      this.storeFormData.emit(data); // Emit response data to parent component
    }
  }
  
  private resetFormState(): void {
    this.makeFormDirty.emit(false);
  }
  

  async sendDataToVoucher() {
    try {
      const formData = await this.serializeFormData(this.itemsForm.value);
      this.storeFormData.emit(formData);
    } catch (error) {
      console.error("Error during form serialization:", error);
    }
  }


  private async serializeFormData(formData: any): Promise<any>{
    const serializedData = {
      ...formData,
      leadId: this.leadId,
      itineraryId: this.itineraryId,
      organizationId: this.storedFormData?.organizationId,
      id: this.storedFormData ? this.storedFormData.id : null,
      createdBy: this.storedFormData ? this.storedFormData.createdBy : null,
      ...(this.storedFormData && { createDate: this.storedFormData.createDate })
    }

    return serializedData;
  }

  async resetForm(){
    if(await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")){
      this.ngOnInit();
    }    
  }

  goPrevious(){
    this.viewChange.emit(true);
  }

  goNext(){
    this.viewChange.emit(false);
  }

  formDataModified(): boolean {
    return (this.itemsForm.dirty || this.itemsForm.invalid) && this.itemsForm.touched;
  }

  setDefaultLov(item:any, type:string, formControlName:string){
    const payload:any = {
      name: item.name
    }
    this._lovDataService.addDefaultLovData(type, payload).subscribe({
      next: (res) => {
        this._responseNotificationService.showMessage("Item added to default list");
        // make addedByUser false for item
        const currentItems = this.itemsForm.get(formControlName)?.value || [];
        const updatedItems = currentItems.map((i: any) => {
          if(i.key === item.key){
            i.addedByUser = false;
          }
          return i;
        });
        this.itemsForm.patchValue({ [formControlName]: updatedItems });
        this.itemsForm.markAsDirty();
        this.itemsForm.markAsTouched();
      },
      error: (err) => {
        console.error("Error adding default", err);
        this._responseNotificationService.showMessage("Error adding default", true);
      }
    });
  }

  async removeDefaultLov(item:any, type:string, formControlName:string){
    let flag = await this._globalService.confirmActionPopup("Removing the item from default list will affect all users in the organization. Please confirm to proceed.", "Confirm to proceed", "Confirm");
    if(!flag){
      return;
    }
    this._lovDataService.removeDefaultLovData(type, item.key).subscribe({
      next: (res) => {
        this._responseNotificationService.showMessage("Item removed from default list");
        const currentItems = this.itemsForm.get(formControlName)?.value || [];
        const updatedItems = currentItems.filter((i: any) => i.key !== item.key);
        this.itemsForm.patchValue({ [formControlName]: updatedItems });
        this.itemsForm.markAsDirty();
        this.itemsForm.markAsTouched();
      },
      error: (err) => {
        console.error("Error removing default", err);
        this._responseNotificationService.showMessage("Error removing default", true);
      }
    });
  }
}
